import type { SearchResult } from '../../types/dictionary'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LabelText, MutedText, MinorHeading } from '@/components/ui/typography'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion'
import { useRadical } from '@/hooks/useRadical'
import { useAlternatePronunciations } from '@/hooks/useAlternatePronunciations'
import { useComponents } from '@/hooks/useComponents'
import { useSettings } from '@/hooks/useSettings'
import { useChineseFont } from '@/lib/chinese-font'
import { cn } from '@/lib/utils'
import { IconLoader2 } from '@tabler/icons-react'

interface Props {
  entry: SearchResult
  onSelectEntry?: (entry: { id: number }) => void
  onSetRadicalFilter?: (radical: number) => void
}

export function CharsTab({ entry, onSelectEntry, onSetRadicalFilter }: Props) {
  const { simplified, traditional, json_data } = entry
  const { settings } = useSettings()
  const chineseFont = useChineseFont()
  const isSingleChar = simplified.length === 1
  // All hooks must be called unconditionally at the top level
  const currentPinyin = entry.pinyin || ''

  // For single characters: radical lookup
  const radical = useRadical(entry.radical ?? null, json_data?.extra_strokes)

  // Alternate pronunciations (only enabled for single chars)
  const { data: alternatePronunciations, isLoading: altLoading } =
    useAlternatePronunciations(
      simplified,
      currentPinyin,
      settings.scriptDisplay === 'traditional' ? 'traditional' : 'simplified'
    )

  // Components lookup (works for both single and multi-char)
  // For single chars: use simplified and scriptDisplay setting
  // For multi-char: use same logic as DICT tab (primary script type)
  const isTraditionalForComponents =
    settings.scriptDisplay === 'traditional' ||
    (settings.scriptDisplay === 'both' &&
      settings.scriptPriority === 'traditional')
  const targetWord =
    isTraditionalForComponents && traditional ? traditional : simplified

  // For single characters: fetch components using simplified
  const { data: componentEntries } = useComponents(
    simplified,
    settings.scriptDisplay === 'traditional'
  )

  // For multi-character words: fetch components using targetWord
  // This will be disabled for single chars (shouldFetch = false in hook)
  const { data: components, isLoading: componentsLoading } = useComponents(
    targetWord,
    isTraditionalForComponents
  )

  // For single characters: show radical, components, alternate pronunciations
  if (isSingleChar) {
    // Get components from json_data or fetched entries
    const componentsFromData = json_data?.components || []
    const displayComponents =
      componentsFromData.length > 0
        ? componentsFromData
        : componentEntries?.map((c) => c.simplified) || []

    return (
      <Accordion
        type="multiple"
        defaultValue={['radical', 'components', 'pronunciations', 'opposite']}
      >
        {/* Radical Info */}
        {radical && (
          <AccordionItem value="radical">
            <AccordionTrigger>
              <MinorHeading className="text-xs tracking-wider uppercase">
                Radical
                {radical.strokes !== undefined &&
                  ` (${radical.strokes} strokes)`}
                {radical.extraStrokes !== undefined &&
                  ` (+ ${radical.extraStrokes} extra)`}
              </MinorHeading>
            </AccordionTrigger>
            <AccordionContent>
              <div className="bg-muted/40 flex justify-between gap-4 rounded-lg p-4">
                <div className="flex gap-4">
                  <div
                    className={cn(
                      chineseFont,
                      'text-foreground text-4xl font-medium'
                    )}
                  >
                    {radical.glyph}
                  </div>
                  <div className="flex flex-col gap-1">
                    {radical.english && (
                      <LabelText className="text-base">
                        {radical.english}
                      </LabelText>
                    )}
                    {radical.pinyin && (
                      <MutedText className="text-sm">
                        {Array.isArray(radical.pinyin)
                          ? radical.pinyin.join(', ')
                          : radical.pinyin}
                      </MutedText>
                    )}
                    {radical.variants && radical.variants.length > 0 && (
                      <MutedText className="text-muted-foreground text-xs">
                        Variants: {radical.variants.join(' ')}
                      </MutedText>
                    )}
                    <MutedText className="text-muted-foreground text-xs">
                      Kangxi #{radical.number}
                    </MutedText>
                  </div>
                </div>
                {onSetRadicalFilter && (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        radical.number && onSetRadicalFilter?.(radical.number)
                      }
                    >
                      Filter by radical
                    </Button>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Components */}
        {displayComponents.length > 0 && (
          <AccordionItem value="components">
            <AccordionTrigger>
              <MinorHeading className="text-xs tracking-wider uppercase">
                Components
              </MinorHeading>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {displayComponents.map((compChar, i) => {
                  // Try to find the component in fetched entries
                  const compEntry = componentEntries?.find(
                    (e) =>
                      e.simplified === compChar || e.traditional === compChar
                  )

                  return (
                    <Card
                      key={i}
                      onClick={() => {
                        if (compEntry) {
                          onSelectEntry?.({ id: compEntry.id })
                        }
                      }}
                      className={cn(
                        'group cursor-pointer border-transparent p-3 transition-all',
                        compEntry
                          ? 'bg-muted/30 hover:border-accent hover:bg-accent/50'
                          : 'bg-muted/20'
                      )}
                    >
                      <CardContent className="flex flex-col items-center gap-2 p-0">
                        <div
                          className={cn(
                            chineseFont,
                            'text-foreground group-hover:text-primary text-2xl transition-colors'
                          )}
                        >
                          {compChar}
                        </div>
                        {compEntry && (
                          <MutedText className="text-xs">
                            {compEntry.pinyin}
                          </MutedText>
                        )}
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Alternate Pronunciations */}
        <AccordionItem value="pronunciations">
          <AccordionTrigger>
            <MinorHeading className="text-xs tracking-wider uppercase">
              Other Pronunciations
            </MinorHeading>
          </AccordionTrigger>
          <AccordionContent>
            {altLoading ? (
              <div className="flex items-center justify-center py-8">
                <IconLoader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : alternatePronunciations &&
              alternatePronunciations.length > 0 ? (
              <div className="space-y-2">
                {alternatePronunciations.map((alt) => (
                  <Card
                    key={alt.id}
                    onClick={() => onSelectEntry?.({ id: alt.id })}
                    className="group bg-muted/30 hover:border-accent hover:bg-accent/50 cursor-pointer border-transparent p-3 transition-all"
                  >
                    <CardContent className="flex items-center gap-3 p-0">
                      <div
                        className={cn(
                          chineseFont,
                          'text-foreground group-hover:text-primary text-xl transition-colors'
                        )}
                      >
                        {simplified}
                      </div>
                      <div className="flex flex-1 flex-col">
                        <LabelText className="text-sm">{alt.pinyin}</LabelText>
                        <MutedText className="truncate text-xs">
                          {alt.preview}
                        </MutedText>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground py-4 text-center text-sm">
                No alternate pronunciations found
              </p>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    )
  }

  // For multi-character words: show characters
  return (
    <div>
      <MinorHeading className="mb-4 text-xs tracking-wider uppercase">
        Characters
      </MinorHeading>
      {componentsLoading && !components ? (
        <div className="flex items-center justify-center py-8">
          <IconLoader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : components && components.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {components.map((comp) => (
            <Card
              key={comp.id}
              onClick={() => onSelectEntry?.({ id: comp.id })}
              className={cn(
                'group bg-muted/30 cursor-pointer border-transparent p-3 transition-all',
                'hover:border-accent hover:bg-accent/50'
              )}
            >
              <CardContent className="flex items-center gap-3 p-0">
                {/* Big Char */}
                <div
                  className={cn(
                    chineseFont,
                    'text-foreground group-hover:text-primary text-2xl transition-colors'
                  )}
                >
                  {isTraditionalForComponents && comp.traditional
                    ? comp.traditional
                    : comp.simplified}
                </div>

                {/* Details */}
                <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                  <div className="text-primary text-sm font-medium">
                    {comp.pinyin}
                  </div>
                  <MutedText className="truncate text-xs">
                    {comp.preview}
                  </MutedText>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground py-4 text-center text-sm">
          No character decomposition available
        </p>
      )}
    </div>
  )
}
